import React, { useState, useEffect, useRef } from 'react';

/* ─── Animation Hook ─── */
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
};

/* ─── Fade-in wrapper ─── */
const FadeIn = ({ children, delay = 0, direction = 'up', style = {} }) => {
  const [ref, inView] = useInView();
  const translateMap = {
    up: 'translateY(28px)',
    down: 'translateY(-28px)',
    left: 'translateX(28px)',
    right: 'translateX(-28px)',
    none: 'none',
  };
  return (
    <div
      ref={ref}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translate(0,0)' : translateMap[direction],
        transition: `opacity 0.65s ease ${delay}s, transform 0.65s ease ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

const bodyText = { fontSize: '15.5px', lineHeight: '1.8', color: '#3d3d3d', margin: 0 };

const StepBlocks = ({ intro, steps }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
    {intro && <p style={{ ...bodyText, marginBottom: '4px' }}>{intro}</p>}
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {steps.map((step) => (
        <div
          key={step.label}
          style={{
            padding: '12px 14px',
            background: '#f9f9f9',
            borderRadius: '4px',
            border: '1px solid #efefef',
          }}
        >
          <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 700, color: '#cc0000' }}>
            {step.label}
          </p>
          <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.7, color: '#444' }}>{step.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

const CAPABILITIES = [
  {
    id: 'graphic-designing',
    title: 'Graphic Designing',
    image: '/Images/C1.jpeg',
    imageAlt: 'Graphic Designing Panel',
    reverse: true,
    content: (
      <p style={bodyText}>
        Decal Tech offers graphic designing support thru its domain expertise.
        Design team possesses a blend of technical knowledge and creative flair
        and exhibits professional approach towards time, cost & deadlines while
        maintaining the project confidentiality. Our team is fully equipped with
        the latest technology to ensure our capabilities to create appealing
        designs & mock-ups.
      </p>
    ),
  },
  {
    id: 'manufacturing',
    title: 'Manufacturing',
    image: '/Images/C2.png',
    imageAlt: 'Advanced Screen Printing Setup',
    reverse: false,
    content: (
      <p style={bodyText}>
        Decal Tech&apos;s manufacturing process spans a variety of technologies that guarantee
        both efficiency and excellence in production. Our state-of-the-art facility in
        Thane, India, is equipped to handle large-scale orders with precision and speed.
        From UV and screen printing to digital production lines, we deliver colour-accurate,
        durable output across automotive graphics, labels, and decals.
      </p>
    ),
  },
  {
    id: 'quality-control-lab',
    title: 'Quality Control Laboratory',
    image: '/Images/C3.png',
    imageAlt: 'QC Inspection Lab Setup',
    reverse: true,
    content: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <p style={bodyText}>
          Quality is at the core of everything we do at Decal Tech. Our dedicated Quality
          Control Laboratory ensures that each product meets the highest standards before
          it reaches our customers. We implement rigorous testing procedures throughout the
          manufacturing process to ensure every product is durable, functional, and visually accurate.
        </p>
        <p style={bodyText}>
          From color testing to material durability, we conduct thorough checks to maintain the
          quality of our graphics, labels, and wraps. Our laboratory is equipped with advanced
          testing equipment including spectrophotometers and adhesion testers that evaluate the
          performance and longevity of each product, guaranteeing reliability and consistency.
        </p>
      </div>
    ),
  },
  {
    id: 'screen-printing',
    title: 'Screen Printing',
    image: '/Images/product9.jpeg',
    imageAlt: 'Screen Printing Production',
    reverse: false,
    content: (
      <StepBlocks
        intro="High-opacity, vibrant screen printing for durable, large-run label production. Our screen-printing capability covers every stage of production:"
        steps={[
          {
            label: 'Pre-Press',
            desc: 'Meticulous preparation of artwork including color separation, image editing, and plate making to ensure flawless prints.',
          },
          {
            label: 'Press (Printing)',
            desc: 'Fully automatic cylinder presses and semi-automatic machines with wicket dryer for efficient, accurate, and consistent printing.',
          },
          {
            label: 'Post-Press',
            desc: 'Cutting, trimming, and finishing treatments that ensure products look great and function flawlessly in their intended applications.',
          },
        ]}
      />
    ),
  },
  {
    id: 'digital-printing',
    title: 'Digital Printing',
    image: '/Images/product8.jpeg',
    imageAlt: 'Digital Printing Setup',
    reverse: true,
    content: (
      <StepBlocks
        intro="Full-colour digital printing for short runs, prototypes, and variable data labels."
        steps={[
          {
            label: 'High-Resolution Output',
            desc: 'Advanced digital printing technology for vibrant colours, sharp graphics, and fast turnaround on any run size.',
          },
          {
            label: 'Flexible & On-Demand',
            desc: 'Perfect for custom small batches or large runs — including automotive graphics and personalized helmet decals.',
          },
        ]}
      />
    ),
  },
  {
    id: 'package-dispatch',
    title: 'Package & Dispatch',
    image: '/Images/C3.jpeg',
    imageAlt: 'Package and Dispatch',
    reverse: false,
    content: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <p style={bodyText}>
          Every order is securely packed, clearly labeled, and dispatched for safe, on-time delivery.
          Our dispatch team follows strict packaging standards to protect decals, labels, and graphics
          during transit — from individual SKU labeling to bulk shipment consolidation.
        </p>
        <p style={bodyText}>
          Documentation accompanies each shipment including batch certificates and delivery manifests,
          ensuring full traceability from production floor to your doorstep.
        </p>
      </div>
    ),
  },
];

/* ─── Capability Row ─── */
const CapabilityRow = ({ id, title, content, image, imageAlt, reverse = false }) => {
  const [imgRef, imgInView] = useInView(0.1);
  const [textRef, textInView] = useInView(0.1);

  return (
    <div id={id} style={{ borderBottom: '1px solid #f0f0f0', padding: '64px 0', scrollMarginTop: '90px' }}>
      <div className="capability-row-inner" style={{
        maxWidth: '1100px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: reverse ? 'row-reverse' : 'row',
        alignItems: 'stretch',
        gap: '48px',
      }}>
        <div className="capability-img-wrap" style={{
          direction: 'ltr',
          flex: '1 1 0',
          minWidth: 0,
          display: 'flex',
        }}>
          <div
            ref={imgRef}
            style={{
              width: '100%',
              minHeight: '280px',
              height: '100%',
              borderRadius: '4px',
              overflow: 'hidden',
              backgroundColor: '#f5f5f5',
              boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
              opacity: imgInView ? 1 : 0,
              transition: 'opacity 0.7s ease 0.25s',
            }}
          >
            <img
              src={image}
              alt={imageAlt}
              style={{ width: '100%', height: '100%', minHeight: '280px', objectFit: 'cover', display: 'block' }}
            />
          </div>
        </div>

        <div
          ref={textRef}
          style={{
            flex: '1 1 0',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            opacity: textInView ? 1 : 0,
            transform: textInView
              ? 'translateX(0)'
              : reverse ? 'translateX(-30px)' : 'translateX(30px)',
            transition: 'opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s',
          }}
        >
          <h2 style={{
            fontSize: 'clamp(1.5rem, 2.2vw, 1.9rem)',
            fontWeight: 800,
            color: '#111',
            lineHeight: 1.2,
            margin: '0 0 14px 0',
          }}>
            {title}
          </h2>
          <div style={{
            height: '3px',
            width: '40px',
            background: '#cc0000',
            borderRadius: '2px',
            marginBottom: '20px',
          }} />
          {content}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Screen ─── */
const OurCapabilitiesscreen = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      className="capabilities-page"
      style={{
      backgroundColor: '#fcfbfa',
      minHeight: '100vh',
      overflowX: 'hidden',
      fontFamily: "'Inter', sans-serif",
    }}>
      <style>{`
        .capabilities-page,
        .capabilities-page * {
          font-family: 'Inter', sans-serif !important;
        }
        @media (max-width: 768px) {
          .capability-row-inner {
            flex-direction: column !important;
          }
          .capability-row-inner .capability-img-wrap {
            min-height: 240px !important;
          }
        }
      `}</style>

      <div style={{ textAlign: 'center', padding: '80px 24px 48px' }}>
        <FadeIn direction="up" delay={0}>
          <h1 style={{
            fontSize: 'clamp(2rem, 4vw, 2.8rem)',
            fontWeight: 800, color: '#111',
            letterSpacing: '-0.5px', margin: '0 0 14px 0',
          }}>
            Our Capabilities
          </h1>
        </FadeIn>
        <FadeIn direction="up" delay={0.12}>
          <p style={{
            fontSize: '16px', lineHeight: '1.8', color: '#555',
            maxWidth: '600px', margin: '0 auto',
          }}>
            At Decal Tech, we combine creative expertise with cutting-edge technology to deliver
            end-to-end solutions — from concept and design to precision manufacturing and rigorous
            quality assurance.
          </p>
        </FadeIn>
      </div>

      <div style={{ padding: '0 5% 100px' }}>
        {CAPABILITIES.map((cap) => (
          <CapabilityRow key={cap.id} {...cap} />
        ))}
      </div>
    </div>
  );
};

export default OurCapabilitiesscreen;
